// =============================================================
// noteMath — the Notes app's inline calculator (the iOS Math Notes idea).
//
// Type an expression and "=" at the end of a line — `1,200 * 12 =` — and the
// answer is written after the "=". Lines earlier in the note that say
// `rent = 1,200` define variables, so `rent * 12 =` works too.
//
// A hand-written parser, never eval(): a note's text is the person's but a
// note can be shared, and eval of note text would run whatever a colleague
// typed. Supports + - * / × ÷ ^, parentheses, unary minus, `%` (5% is 0.05,
// so `200 * 5% =` is 10), thousands commas, and a leading `$` (which makes
// the answer dollars, to the cent).
//
// Used by the web editor (NoteBodyEditor `mathAssist`) and the iOS DOM editor.
// =============================================================

type Tok = { t: "num"; v: number; money: boolean } | { t: "op"; v: string } | { t: "id"; v: string };

const OPS = new Set(["+", "-", "*", "/", "^", "(", ")"]);

function tokenize(src: string): Tok[] | null {
  const out: Tok[] = [];
  let i = 0;
  // "×" is always multiply; a letter x only between numbers ("3 x 4"), so a
  // variable like `tax` is left alone.
  const s = src.replace(/×/g, "*").replace(/([\d)%])\s*x\s*(?=[\d$(.])/g, "$1*").replace(/÷/g, "/").replace(/−/g, "-");
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === "$" || /[\d.]/.test(c)) {
      let money = false;
      if (c === "$") { money = true; i++; }
      const m = /^(\d{1,3}(,\d{3})+(\.\d+)?|\d*\.?\d+)/.exec(s.slice(i));
      if (!m) return null;
      let v = Number(m[0].replace(/,/g, ""));
      i += m[0].length;
      const suffix = /^\s*([kKmMbB])(?![a-zA-Z])/.exec(s.slice(i));
      if (suffix) {
        v *= { k: 1e3, m: 1e6, b: 1e9 }[suffix[1].toLowerCase() as "k" | "m" | "b"];
        i += suffix[0].length;
      }
      if (s[i] === "%") { v /= 100; i++; }
      out.push({ t: "num", v, money });
      continue;
    }
    if (OPS.has(c)) { out.push({ t: "op", v: c }); i++; continue; }
    const id = /^[A-Za-z_][A-Za-z0-9_]*/.exec(s.slice(i));
    if (id) { out.push({ t: "id", v: id[0].toLowerCase() }); i += id[0].length; continue; }
    return null;
  }
  return out;
}

interface Val { v: number; money: boolean }

function parse(tokens: Tok[], vars: Record<string, Val>): Val | null {
  let pos = 0;
  const peek = () => tokens[pos];
  const isOp = (v: string) => { const t = peek(); return !!t && t.t === "op" && t.v === v; };

  function primary(): Val | null {
    const t = peek();
    if (!t) return null;
    if (t.t === "num") { pos++; return { v: t.v, money: t.money }; }
    if (t.t === "id") { pos++; const x = vars[t.v]; return x ? { ...x } : null; }
    if (isOp("(")) {
      pos++;
      const v = expr();
      if (!v || !isOp(")")) return null;
      pos++;
      return v;
    }
    if (isOp("-")) { pos++; const v = unary(); return v ? { v: -v.v, money: v.money } : null; }
    if (isOp("+")) { pos++; return unary(); }
    return null;
  }
  function unary(): Val | null { return power(); }
  function power(): Val | null {
    const base = primary();
    if (!base) return null;
    if (isOp("^")) {
      pos++;
      const e = unary();
      if (!e) return null;
      return { v: Math.pow(base.v, e.v), money: base.money };
    }
    return base;
  }
  function term(): Val | null {
    let a = unary();
    while (a && (isOp("*") || isOp("/"))) {
      const op = (peek() as any).v;
      pos++;
      const b = unary();
      if (!b) return null;
      a = op === "*" ? { v: a.v * b.v, money: a.money || b.money } : { v: a.v / b.v, money: a.money && !b.money };
    }
    return a;
  }
  function expr(): Val | null {
    let a = term();
    while (a && (isOp("+") || isOp("-"))) {
      const op = (peek() as any).v;
      pos++;
      const b = term();
      if (!b) return null;
      a = { v: op === "+" ? a.v + b.v : a.v - b.v, money: a.money || b.money };
    }
    return a;
  }
  const v = expr();
  if (!v || pos !== tokens.length || !Number.isFinite(v.v)) return null;
  return v;
}

/** Evaluate one expression; null when it is not a calculation. */
export function evaluateNoteMath(src: string, vars: Record<string, Val> = {}): Val | null {
  const tokens = tokenize(String(src || ""));
  if (!tokens || !tokens.length) return null;
  return parse(tokens, vars);
}

export function formatNoteMath(val: Val): string {
  if (val.money) {
    const n = Math.round(val.v * 100) / 100;
    const s = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${n < 0 ? "-" : ""}$${s}`;
  }
  const r = Math.round(val.v * 1e6) / 1e6;
  return r.toLocaleString("en-US", { maximumFractionDigits: 6 });
}

const ASSIGN_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.+?)\s*$/;

/** Variables defined by `name = expression` lines, in order (later wins). */
export function noteMathVariables(lines: string[]): Record<string, Val> {
  const vars: Record<string, Val> = {};
  for (const line of lines) {
    const m = ASSIGN_RE.exec(line || "");
    if (!m) continue;
    const v = evaluateNoteMath(m[2], vars);
    if (v) vars[m[1].toLowerCase()] = v;
  }
  return vars;
}

/**
 * The answer to write after a line that ends with "=", or null. `line` is the
 * text up to and including the "="; `before` is every line above it.
 *
 * The expression is the LONGEST tail of the line that is a calculation and
 * does something (an operator, or a variable) — so `Annual rent: 1,200 * 12 =`
 * computes `1,200 * 12`, and a bare `Total =` or `Year 2026 =` writes nothing.
 */
export function noteMathAnswer(line: string, before: string[] = []): string | null {
  const text = String(line || "");
  if (!/=\s*$/.test(text)) return null;
  const body = text.replace(/=\s*$/, "");
  if (/[=<>]/.test(body)) return null;
  const vars = noteMathVariables(before);
  const starts: number[] = [0];
  for (let i = 1; i < body.length; i++) if (/\s/.test(body[i - 1]) || /[(:]/.test(body[i - 1])) starts.push(i);
  for (const st of starts) {
    const tail = body.slice(st).trim();
    if (!tail) continue;
    const tokens = tokenize(tail);
    if (!tokens) continue;
    const doesSomething = tokens.some((t) => t.t === "id" || (t.t === "op" && t.v !== "(" && t.v !== ")"));
    if (!doesSomething) continue;
    // A leading minus alone ("-5 =") is a number, not a calculation.
    if (tokens.length === 2 && tokens[0].t === "op" && tokens[0].v === "-" && tokens[1].t === "num") continue;
    const v = parse(tokens, vars);
    if (v) return formatNoteMath(v);
  }
  return null;
}

function inlineText(content: any): string {
  return Array.isArray(content) ? content.map((c: any) => (typeof c === "string" ? c : c?.text || (Array.isArray(c?.content) ? inlineText(c.content) : ""))).join("") : "";
}

/**
 * The editor half, shared by the web editor and the iOS DOM editor: call the
 * returned function from BlockNote's onChange. It acts only when the person
 * has JUST typed "=" (the text before the cursor grew by exactly "="), so undo,
 * paste, a co-editor's change or opening a note full of "=" lines never writes
 * an answer. The answer is inserted at the cursor as ordinary text — undo
 * takes it back out.
 */
export function createMathAssist() {
  let last: { id: string; before: string } | null = null;
  return function onChange(editor: any): void {
    try {
      const sel = editor?.prosemirrorState?.selection;
      if (!sel || !sel.empty) { last = null; return; }
      const $from = sel.$from;
      const before: string = $from.parent.textBetween(0, $from.parentOffset, "\n", " ");
      const block = editor.getTextCursorPosition()?.block;
      if (!block) return;
      const prev = last;
      last = { id: block.id, before };
      if (!prev || prev.id !== block.id || before !== `${prev.before}=`) return;
      const lines: string[] = [];
      const walk = (blocks: any[]): boolean => {
        for (const b of blocks || []) {
          if (b.id === block.id) return true;
          lines.push(inlineText(b.content));
          if (b.children?.length && walk(b.children)) return true;
        }
        return false;
      };
      walk(editor.document);
      const answer = noteMathAnswer(before, lines);
      if (!answer) return;
      const insert = before.endsWith(" =") ? ` ${answer}` : answer;
      editor.insertInlineContent(insert);
      last = { id: block.id, before: before + insert };
    } catch { /* the calculator must never break typing */ }
  };
}
